// Grid/Pagination.js
import React from 'react';
import PropTypes from 'prop-types';
import { TablePagination } from '@mui/material';

/**
 * Pagination Component
 *
 * A reusable pagination component that wraps Material-UI's TablePagination.
 * It handles page changes and rows-per-page changes.
 *
 * @component
 * @param {object} props - Component props.
 * @param {number} props.page - Current page number (zero-based).
 * @param {number} props.rowsPerPage - Number of rows per page.
 * @param {number} props.totalCount - Total number of rows.
 * @param {function} props.onChangePage - Handler for page change.
 * @param {function} props.onChangeRowsPerPage - Handler for rows-per-page change.
 * @param {array<number>} [props.rowsPerPageOptions=[5,10,25]] - Options for rows per page.
 * @param {function} [props.labelDisplayedRows=({ from, to, count }) => `${from}-${to} of ${count}`] - Label for displayed rows.
 * @param {string} [props.labelRowsPerPage='Rows per page:'] - Label for rows per page selector.
 *
 * @returns {JSX.Element} The Pagination component.
 */
function Pagination({
	page,
	rowsPerPage,
	totalCount,
	onChangePage,
	onChangeRowsPerPage,
	rowsPerPageOptions = [5, 10, 25],
	labelDisplayedRows = ({ from, to, count }) => `${from}-${to} of ${count}`,
	labelRowsPerPage = 'Rows per page:',
}) {
	return (
		<TablePagination
			component="div"
			count={totalCount}
			page={page}
			onPageChange={onChangePage}
			rowsPerPage={rowsPerPage}
			onRowsPerPageChange={onChangeRowsPerPage}
			rowsPerPageOptions={rowsPerPageOptions}
			labelDisplayedRows={labelDisplayedRows}
			labelRowsPerPage={labelRowsPerPage}
		/>
	);
}

Pagination.propTypes = {
	/** Current page number (zero-based). */
	page: PropTypes.number.isRequired,

	/** Number of rows per page. */
	rowsPerPage: PropTypes.number.isRequired,

	/** Total number of rows. */
	totalCount: PropTypes.number.isRequired,

	/**
	 * Handler function called when the page is changed.
	 *
	 * Signature: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void
	 */
	onChangePage: PropTypes.func.isRequired,

	/**
	 * Handler function called when the rows per page is changed.
	 *
	 * Signature: (event: React.ChangeEvent<HTMLInputElement>) => void
	 */
	onChangeRowsPerPage: PropTypes.func.isRequired,

	/** Options for the number of rows per page. */
	rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),

	/** Custom label for displayed rows. */
	labelDisplayedRows: PropTypes.func,

	/** Custom label for rows per page selector. */
	labelRowsPerPage: PropTypes.string,
};

export default Pagination;
